import { useEffect, useState } from "react";

interface UseFontLoaderProps {
  fontFileUrl: string;
  fontFamily: string;
  fontWeight?: string;
  fontStyle?: "normal" | "italic";
  fontId?: string;
  persistOnUnmount?: boolean;
}

interface UseFontLoaderResult {
  isLoaded: boolean;
  error: string | null;
}

/**
 * Custom hook for dynamically loading fonts.
 *
 * @param fontFileUrl - URL to the font file to load
 * @param fontFamily - Base font family name
 * @param fontWeight - Font weight (optional, defaults to "normal")
 * @param fontStyle - Font style (optional, defaults to "normal")
 * @param fontId - Optional unique identifier for the font (useful when loading multiple styles of the same font)
 * @param persistOnUnmount - If true, the font will remain loaded even after the component unmounts
 * @returns Object containing isLoaded state and fontFamilyName to use in CSS
 */
export const useFontLoader = ({
  fontFileUrl,
  fontFamily,
  fontWeight = "normal",
  fontStyle = "normal",
  fontId,
  persistOnUnmount = false,
}: UseFontLoaderProps): UseFontLoaderResult => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fontFileUrl) {
      console.warn("No font URL provided to useFontLoader");
      return;
    }

    // Create a style element to inject the @font-face declaration
    const styleElement = document.createElement("style");
    styleElement.setAttribute("type", "text/css");
    styleElement.setAttribute("data-font-loader", fontFamily);

    // Determine font format from URL
    const getFormatFromUrl = (url: string): string => {
      if (url.includes(".woff2")) return "woff2";
      if (url.includes(".woff")) return "woff";
      if (url.includes(".ttf")) return "truetype";
      if (url.includes(".otf")) return "opentype";
      return "truetype"; // default to truetype
    };

    const fetchAndLoadFont = async () => {
      try {
        // Fetch the font file to handle CORS
        const response = await fetch(fontFileUrl, {
          mode: "cors", // Explicitly request CORS mode
          credentials: "omit", // Don't send credentials for cross-origin requests to S3
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch font: ${response.status} ${response.statusText}`,
          );
        }

        // Get the font data as a blob
        const fontBlob = await response.blob();

        // Create an object URL from the blob
        const fontObjectUrl = URL.createObjectURL(fontBlob);

        // Create the @font-face CSS with the object URL instead of the original URL
        const fontFaceCSS = `
          @font-face {
            font-family: "${fontFamily}";
            src: url("${fontObjectUrl}") format("${getFormatFromUrl(fontFileUrl)}");
            font-weight: ${fontWeight};
            font-style: ${fontStyle};
            font-display: swap;
          }
        `;

        styleElement.textContent = fontFaceCSS;
        document.head.append(styleElement);

        // Create a Font object to track loading
        const font = new FontFace(fontFamily, `url("${fontObjectUrl}")`, {
          weight: fontWeight,
          style: fontStyle,
        });

        // Load the font
        await font.load();

        // Add the font to the document's fonts collection
        document.fonts.add(font);
        setIsLoaded(true);
        setError(null);

        // Store the objectURL for cleanup
        return fontObjectUrl;
      } catch (err) {
        console.error(`Failed to load font ${fontFamily}:`, err);
        setError((err as Error).message || "Failed to load font");
        return null;
      }
    };

    // Call the fetch and load function
    let fontObjectUrl: string | null = null;
    fetchAndLoadFont()
      .then((url) => {
        fontObjectUrl = url;
      })
      .catch((err) => {
        console.error("Error in fetchAndLoadFont:", err);
        setError((err as Error).message || "Failed to load font");
      });

    // Clean up on unmount (only if persistOnUnmount is false)
    return () => {
      if (!persistOnUnmount) {
        // Remove the style element
        if (document.head.contains(styleElement)) {
          styleElement.remove();
        }

        // Revoke the object URL if it was created
        if (fontObjectUrl) {
          URL.revokeObjectURL(fontObjectUrl);
        }

        // Try to remove the font from document fonts if possible
        try {
          for (const f of document.fonts) {
            if (f.family === fontFamily) {
              document.fonts.delete(f);
            }
          }
        } catch (err) {
          console.warn("Could not remove font from document.fonts:", err);
        }
      }
    };
  }, [fontFileUrl, fontFamily, fontWeight, fontStyle, persistOnUnmount]);

  return { isLoaded, error };
};
